import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { focusManager, QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { businessResourceKey, BusinessScope } from "../utils/businessResourceFreshness";

interface ResourceOptions<T> extends BusinessScope {
  scopeLevel?: "warehouse" | "company" | "user";
  resource: string;
  parameters?: readonly unknown[];
  enabled: boolean;
  active: boolean;
  load: (signal: AbortSignal) => Promise<T>;
  staleTime?: number;
  networkMode?: "online" | "always";
}

const refreshes = new WeakMap<QueryClient, Map<string, Promise<unknown>>>();
let focusEventsInstalled = false;

function installBusinessFocusEvents() {
  if (focusEventsInstalled || typeof window === "undefined") return;
  focusEventsInstalled = true;
  // React Query v5 listens to visibilitychange by default. A still-visible
  // browser window also needs to refresh when returning from another app.
  // One shared manager deduplicates visibility + focus events for every key.
  focusManager.setEventListener((setFocused) => {
    const onFocus = () => setFocused(document.visibilityState !== "hidden");
    const onBlur = () => setFocused(false);
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    window.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("visibilitychange", onFocus);
    };
  });
}

export default function useBusinessResource<T>({
  resource, userId, companyId, warehouseId, moduleScope = "commercial",
  parameters = [], enabled, active, load, staleTime = 30_000, networkMode = "online", scopeLevel = "warehouse",
}: ResourceOptions<T>) {
  const client = useQueryClient();
  useEffect(installBusinessFocusEvents, []);
  const keyText = JSON.stringify(businessResourceKey(resource,
    { userId, companyId, warehouseId, moduleScope }, parameters));
  const queryKey = useMemo(() => JSON.parse(keyText), [keyText]);
  // Account security resources belong to a user, independently of tenant selection.
  // Company/warehouse resources keep their existing mandatory tenant checks.
  const canLoad = Boolean(enabled && userId && (scopeLevel === "user" ||
    (companyId && (scopeLevel === "company" || warehouseId))));
  const current = useRef({ keyText, canLoad, active });
  current.current = { keyText, canLoad, active };
  const [activeKey, setActiveKey] = useState("");
  const queryFn = ({ signal }: { signal: AbortSignal }) => load(signal);
  const query = useQuery<T>({
    // The opening effect owns the first active read. Enabling the observer
    // after cancellation avoids starting a request only to cancel it at once.
    queryKey, queryFn, enabled: canLoad && (!active || activeKey === keyText), staleTime, gcTime: 5 * 60_000, networkMode,
    retry: 1, refetchOnMount: "always",
    refetchOnWindowFocus: active ? "always" : false,
    refetchOnReconnect: active ? "always" : false,
  });
  const latestLoad = useRef(load);
  latestLoad.current = load;
  const refresh = useCallback(async ({ force = false }: { force?: boolean } = {}) => {
    if (!canLoad) throw new Error("Contexte de chargement indisponible.");
    // A pending first request may describe the page before the form was opened.
    // Explicit cancellation is needed even when that query has no cached data.
    let pending = refreshes.get(client);
    if (!pending) { pending = new Map(); refreshes.set(client, pending); }
    let operation = force ? undefined : pending.get(keyText);
    if (!operation) {
      const loadForKey = latestLoad.current;
      operation = (async () => {
        await client.cancelQueries({ queryKey, exact: true });
        // Another consumer may still need this key after its initiator moves
        // to a different context. Keep the shared operation bound to the
        // captured loader, then check context separately for every caller.
        if (!client.getQueryCache().find({ queryKey, exact: true })?.getObserversCount()) {
          throw new Error("Le contexte a changé. Rouvrez le formulaire.");
        }
        if (current.current.keyText === keyText && current.current.canLoad && current.current.active) {
          setActiveKey(keyText);
        }
        return client.fetchQuery<T>({ queryKey,
          queryFn: ({ signal }) => loadForKey(signal), staleTime: 0, retry: 1, networkMode,
        });
      })();
      pending.set(keyText, operation);
      const registered = operation;
      void operation.finally(() => {
        if (pending!.get(keyText) === registered) pending!.delete(keyText);
      }).catch(() => {});
    }
    const data = await operation as T;
    if (current.current.keyText !== keyText || !current.current.canLoad) {
      throw new Error("Le contexte a changé. Rouvrez le formulaire.");
    }
    if (current.current.active) setActiveKey(keyText);
    return data;
  }, [canLoad, client, keyText, queryKey, networkMode]);
  useEffect(() => {
    if (canLoad && active) void refresh().catch(() => {});
    else setActiveKey("");
  }, [canLoad, active, refresh]);
  return {
    data: canLoad ? query.data : undefined,
    loading: canLoad && (query.isFetching || (active && activeKey !== keyText)),
    status: !canLoad || query.data === undefined ? "unavailable" as const
      : query.isError || query.isFetching || (active && activeKey !== keyText) ? "cached" as const : "verified" as const,
    error: canLoad && query.isError ? query.error : null,
    updatedAt: query.dataUpdatedAt,
    refresh,
  };
}
